import { useMemo, useRef, useState } from "react";
import { initTyro } from "../lib/tyroService";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
  Receipt, Download, Pulse, ArrowSquareOut, Plug, Calendar, X,
  CircleNotch, Warning, FileText, FileCode,
} from "@phosphor-icons/react";

/**
 * Tyro Reconciliation card — Tyro cert "Integrated Reports".
 *
 * Cert criteria implemented (matrix Recon.Report.1 / .2 / .3 / .4 / .5):
 *
 *   Recon.Report.1 — POS exposes a UI to initiate reconciliationReport
 *                    with date (any day in the last 7 business days),
 *                    report type (summary | detail) and format
 *                    (text | xml — text required, xml optional).
 *   Recon.Report.2 — Progress modal while the report is being generated.
 *   Recon.Report.3 — On failure (responseReceivedCallback returns
 *                    failure) display BOTH the result AND the error
 *                    message in a modal.
 *   Recon.Report.4 — Parse out preformatted-text labels
 *                    (MEDIUM_CENTRED, NEW_LINE, MEDIUM, etc.) from the
 *                    text body before display / print.
 *   Recon.Report.5 — Parse the XML body (if XML requested) and surface
 *                    the data in a readable form. Either display on
 *                    screen or print — we do both.
 *
 * The legacy "local DB fallback" reconciliation builder that this card
 * used to fall through to has been removed — the cert requires the
 * iClient `reconciliationReport()` SDK call to be the source of truth.
 */
export default function TyroReconciliationCard() {
  const { currentStore } = useStore();

  // ─── Form state ─────────────────────────────────────────────────────
  const [reportType, setReportType] = useState("detail"); // summary | detail
  const [format,     setFormat]     = useState("txt");    // txt | xml
  const [businessDay, setBusinessDay] = useState(""); // ISO date — defaults to today

  // ─── Runtime state ──────────────────────────────────────────────────
  const [progress, setProgress] = useState(null); // null | { status, msg }
  const [result,   setResult]   = useState(null); // { type, format, businessDay, raw, parsed, receivedAt }
  const [errorBox, setErrorBox] = useState(null); // { result, error } — Recon.Report.3 modal
  const [termInfo, setTermInfo] = useState(null);
  const startedAtRef = useRef(null);

  const canRun = !!currentStore?.tyro_enabled && !!currentStore?.tyro_integration_key;

  // ─── Business-day picker — last 7 business days (Mon–Fri) ───────────
  // Recon.Report.1: "date - any day in last 7 business days".
  //
  // CRITICAL: must use the user's LOCAL date (Australia/Brisbane for our
  // franchises), not the UTC date. Previously we used
  // `d.toISOString().slice(0, 10)` which is UTC — for any AEST time
  // between 00:00 and 09:59 the UTC date is the previous day, so the
  // dropdown silently shifted every option by one business day and the
  // Tyro acquirer rejected the request (or returned the wrong day's
  // data). The user reported this as "the last 2 dates are missing".
  const lastBusinessDays = useMemo(() => {
    const toLocalIso = (dt) => {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const d = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };
    const out = [];
    let d = new Date();
    while (out.length < 7) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        const iso = toLocalIso(d);
        const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        out.push({ iso, label: out.length === 0 ? `Today (${label})` : label });
      }
      d = new Date(d.getTime() - 24 * 60 * 60 * 1000);
    }
    return out;
  }, []);

  // ─── Recon.Report.4: strip preformatted-text labels ─────────────────
  // Tyro txt reports come back with sentinel words intended for their
  // own printer driver — MEDIUM, MEDIUM_CENTRED, NEW_LINE, LARGE, SMALL,
  // LARGE_CENTRED, etc. We remove the labels (replacing NEW_LINE with
  // an actual line break) so the on-screen and printed output is
  // readable.
  const stripFormattingLabels = (raw) => {
    if (!raw || typeof raw !== "string") return raw;
    return raw
      .replace(/NEW_LINE/g, "\n")
      .replace(/\b(MEDIUM_CENTRED|MEDIUM_CENTERED|LARGE_CENTRED|LARGE_CENTERED|SMALL_CENTRED|SMALL_CENTERED|MEDIUM|LARGE|SMALL|BOLD|UNDERLINE|RIGHT_ALIGN|LEFT_ALIGN|CENTRED|CENTERED)\b/g, "")
      // collapse runs of >2 blank lines + trim
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // ─── Recon.Report.5 (optional): pretty-print XML ────────────────────
  // We pull every <tag>value</tag> pair under any common parent and
  // surface them as a key/value table. Falls back to indented XML if
  // the structure is too irregular to flatten.
  const parseXmlReport = (raw) => {
    if (!raw || typeof raw !== "string") return { kv: null, pretty: raw };
    try {
      const doc = new DOMParser().parseFromString(raw, "application/xml");
      if (doc.querySelector("parsererror")) return { kv: null, pretty: raw };
      const kv = [];
      const walk = (node, path = "") => {
        for (const child of node.children) {
          const name = child.tagName;
          const here = path ? `${path}.${name}` : name;
          if (child.children.length === 0) {
            const v = (child.textContent || "").trim();
            if (v) kv.push({ key: here, value: v });
          } else {
            walk(child, here);
          }
        }
      };
      walk(doc.documentElement);
      // Indent the original XML for the "raw" tab.
      const pretty = raw
        .replace(/></g, ">\n<")
        .split("\n")
        .reduce((acc, line) => {
          const isClose = /^<\//.test(line);
          if (isClose) acc.depth -= 1;
          acc.out.push("  ".repeat(Math.max(0, acc.depth)) + line);
          if (/^<[^/!?][^>]*[^/]>$/.test(line)) acc.depth += 1;
          return acc;
        }, { depth: 0, out: [] }).out.join("\n");
      return { kv: kv.length ? kv : null, pretty };
    } catch {
      return { kv: null, pretty: raw };
    }
  };

  // ─── Recon.Report.1 + .2 + .3: fire the SDK call ────────────────────
  const fetchReport = async () => {
    if (!canRun) return toast.error("Pair a Tyro terminal in /settings/tyro first");
    setResult(null);
    setErrorBox(null);
    setProgress({ status: "init", msg: "Initialising SDK…" });
    startedAtRef.current = Date.now();

    try {
      // Fetch the SDK API key from the backend — same SaaS pattern as the
      // payment dialog. After the v3.48 transition the per-store
      // `tyro_integration_key` is the PAIRING token (long opaque string
      // minted by `tyro.pair()`), NOT the SDK API key. Passing it to
      // initTyro silently disables the SDK auth on prod and the
      // reconciliationReport() callback never fires → "Generating
      // reconciliation…" spins forever. This bug was reported by the
      // user on production after v3.48.
      const sdkKeyResp = await api.get(`/stores/${currentStore.id}/tyro/sdk-key`);
      const sdkApiKey = sdkKeyResp?.data?.api_key || "";
      const sdkEnv    = sdkKeyResp?.data?.env || currentStore.tyro_env || "test";
      const tyro = await initTyro({
        env: sdkEnv,
        apiKey: sdkApiKey,
        posProduct: {
          vendor: "iRepairExperts", name: "iRepairPOS", version: "1.0.0",
          siteReference: currentStore?.name || currentStore?.id || "default",
        },
      });

      setProgress({ status: "running", msg: "Pulling report from acquirer…" });

      const r = await tyro.reconciliationReport({
        mid: currentStore.tyro_merchant_id,
        tid: currentStore.tyro_terminal_id,
        integrationKey: currentStore.tyro_integration_key,
        type: reportType,                         // summary | detail
        format,                                   // txt | xml
        businessDay: businessDay || undefined,    // ISO yyyy-mm-dd, omit for "today"
      });

      // tyroService.reconciliationReport() already unwraps the SDK
      // firmware variations and gives us a stable shape:
      //   { ok: true, raw, format, type, businessDay, currentDay, receivedAt, _rawSdkResponse }
      // — `raw` is the report body (first non-empty of report/data/text/xml).
      const sdkBody = r?.raw || "";
      if (!sdkBody) {
        // SDK said success but no body — usually means Tyro really has
        // nothing to reconcile (empty day). Don't render an empty
        // "(empty report)" pane; surface the same friendly modal that
        // the FAILURE path uses so the cashier knows what to do next.
        setProgress(null);
        console.warn("[recon] empty body from SDK. Full payload:", r?._rawSdkResponse);
        setErrorBox({
          result: "EMPTY",
          error:  "Tyro returned no data for this business day. Try a different day or switch report type.",
          raw:    r?._rawSdkResponse || null,
        });
        return;
      }

      const cleaned = format === "xml" ? sdkBody : stripFormattingLabels(sdkBody);
      const parsed  = format === "xml" ? parseXmlReport(sdkBody) : null;

      setResult({
        type: reportType,
        format,
        businessDay: businessDay || "today",
        raw: sdkBody,
        cleaned,
        parsed,
        receivedAt: new Date().toISOString(),
        durationMs: Date.now() - (startedAtRef.current || Date.now()),
      });
      toast.success("Reconciliation report received");
    } catch (e) {
      console.error("[recon] SDK call failed:", e);
      // Recon.Report.3: failure of the responseReceivedCallback must
      // surface both the result and the error message in a modal.
      setErrorBox({
        result: (e?.result || "FAILURE").toString().toUpperCase(),
        error:  e?.error || e?.message || "Terminal unreachable",
        raw:    e?.raw || null,
      });
    } finally {
      setProgress(null);
    }
  };

  // ─── Terminal status (Tyro Recommended — not part of Recon cert) ────
  const checkTerminal = async () => {
    if (!canRun) return toast.error("Pair a Tyro terminal first");
    try {
      // Same SaaS-aware SDK key fetch as fetchReport — never use
      // tyro_integration_key as the SDK API key.
      const sdkKeyResp = await api.get(`/stores/${currentStore.id}/tyro/sdk-key`);
      const sdkApiKey = sdkKeyResp?.data?.api_key || "";
      const sdkEnv    = sdkKeyResp?.data?.env || currentStore.tyro_env || "test";
      const tyro = await initTyro({
        env: sdkEnv,
        apiKey: sdkApiKey,
        posProduct: { vendor: "iRepairExperts", name: "iRepairPOS",
                       version: "1.0.0", siteReference: currentStore?.name || currentStore?.id || "default" },
      });
      let info;
      try {
        info = await tyro.terminalInfo({
          mid: currentStore.tyro_merchant_id,
          tid: currentStore.tyro_terminal_id,
          integrationKey: currentStore.tyro_integration_key,
        });
      } catch (e1) {
        console.warn("[recon] terminalInfo with params failed, retrying without:", e1?.message);
        info = await tyro.terminalInfo({ omitParams: true });
      }
      setTermInfo(info);
      toast.success("Terminal online");
    } catch (e) {
      console.error("[recon] terminalInfo failed:", e);
      setTermInfo({ ok: false, error: e?.message, raw: e?.raw });
      toast.error(`Terminal unreachable: ${e?.message || "unknown"}`);
    }
  };

  // ─── PDF export — uses the post-clean text body so the file matches
  //     what the cashier sees on screen. Available for print as well.
  const downloadPdf = async () => {
    if (!result) return;
    try {
      const r = await api.post(
        `/tyro/reconciliation/pdf`,
        {
          store_id: currentStore.id,
          mid: currentStore.tyro_merchant_id,
          tid: currentStore.tyro_terminal_id,
          type: result.type,
          current_day: result.businessDay === "today",
          received_at: result.receivedAt,
          // Send the cleaned text — formatting labels stripped per
          // Recon.Report.4 so the PDF matches the on-screen output.
          report_text: result.cleaned || result.raw,
        },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tyro-reconciliation-${currentStore.tyro_merchant_id}-${result.businessDay}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid="tyro-recon-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.14em] uppercase text-[#6B7280] font-semibold">
            Tyro · Integrated reports
          </div>
          <div className="font-display font-semibold text-[18px] mt-0.5 tracking-tight">
            Reconciliation report
          </div>
          <div className="text-[12px] text-[#4B5563] max-w-2xl mt-1 leading-relaxed">
            End-of-day reconciliation pulled live from the Tyro acquirer
            via the iClient SDK. Pick any day in the last 7 business days
            and the report type / format. Required for Tyro cert
            <em> Recon.Report.1 / .2 / .3 / .4 / .5</em>.
          </div>
        </div>
        {currentStore?.tyro_merchant_id && (
          <div className="text-[10px] font-mono text-[#6B7280] shrink-0">
            MID {currentStore.tyro_merchant_id} · TID {currentStore.tyro_terminal_id}
          </div>
        )}
      </div>

      {/* ─── Recon.Report.1 form ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 p-4 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA]">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">Business day</span>
          <select
            value={businessDay || lastBusinessDays[0].iso}
            onChange={(e) => setBusinessDay(e.target.value)}
            className="h-9 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-[#121216]"
            data-testid="tyro-recon-business-day"
          >
            {lastBusinessDays.map((d) => (
              <option key={d.iso} value={d.iso}>{d.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">Report type</span>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="h-9 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-[#121216]"
            data-testid="tyro-recon-type"
          >
            <option value="summary">Summary</option>
            <option value="detail">Detail (itemised)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold">Format</span>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="h-9 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-[#121216]"
            data-testid="tyro-recon-format"
          >
            <option value="txt">Text</option>
            <option value="xml">XML (optional)</option>
          </select>
        </label>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={fetchReport}
            disabled={!canRun || !!progress}
            data-testid="tyro-recon-run"
            className="h-9 flex-1 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] disabled:bg-slate-300 text-white text-[12px] font-bold inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            <Receipt size={12} weight="bold" /> Get report
          </button>
          <button
            type="button"
            onClick={checkTerminal}
            disabled={!canRun}
            data-testid="tyro-recon-health"
            className="h-9 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#121216] text-[#4B5563] hover:text-[#121216] text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            title="Terminal status (terminalInfo)"
          >
            <Pulse size={12} weight="bold" />
          </button>
        </div>
      </div>

      {!canRun && (
        <div className="text-[12px] text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2">
          Pair a Tyro terminal in <a href="/settings/tyro" className="underline font-semibold">/settings/tyro</a> before running reports.
        </div>
      )}

      {/* ─── Recon.Report.5 (terminalInfo failure flow — kept) ─── */}
      {termInfo && !termInfo.ok && (
        <div className="mt-3 rounded-xl border-2 border-[#FBBF24] bg-[#FFFBEB] px-4 py-3" data-testid="tyro-pair-diagnostic">
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#92400E] mb-1">Terminal not reachable</div>
          <p className="text-[12px] text-[#78350F] leading-snug">The SDK can&apos;t talk to the simulator. Re-pair the terminal then retry.</p>
          <div className="flex gap-2 mt-3">
            <a href="https://iclientsimulator.test.tyro.com/configuration.html#expert" target="_blank" rel="noopener noreferrer" className="h-8 px-3 rounded-md bg-[#F59E0B] hover:bg-[#D97706] text-white text-[11px] font-bold inline-flex items-center gap-1.5">
              <ArrowSquareOut size={11} weight="bold" /> Open simulator
            </a>
            <a href="/settings/tyro" className="h-8 px-3 rounded-md border border-[#F59E0B] text-[#92400E] hover:bg-[#FDE68A] text-[11px] font-bold inline-flex items-center gap-1.5">
              <Plug size={11} weight="bold" /> Pair terminal
            </a>
          </div>
        </div>
      )}

      {termInfo && termInfo.ok && (
        <div className="mt-3 text-[11px] font-mono bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3" data-testid="tyro-terminal-ok">
          <div className="font-bold text-[#065F46] mb-1 flex items-center gap-1.5"><Pulse size={12} weight="bold" /> Terminal online</div>
          <pre className="whitespace-pre-wrap break-all text-[10px] text-[#047857]">{JSON.stringify(termInfo, null, 2)}</pre>
        </div>
      )}

      {/* ─── Recon.Report.1 result panel ────────────────────────── */}
      {result && (
        <div className="mt-4" data-testid="tyro-recon-result">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[11px] text-[#4B5563] flex items-center gap-1.5">
              <Calendar size={12} weight="bold" />
              {result.type === "summary" ? "Summary" : "Detail"} · {result.format.toUpperCase()} ·{" "}
              {result.businessDay === "today" ? "Today" : result.businessDay} ·{" "}
              {new Date(result.receivedAt).toLocaleString()} ·{" "}
              <span className="font-mono">{result.durationMs}ms</span>
            </div>
            <button
              onClick={downloadPdf}
              data-testid="tyro-recon-download"
              className="h-8 px-3 rounded-md border border-[#E5E7EB] hover:border-[#121216] text-[#4B5563] hover:text-[#121216] text-[11px] font-semibold inline-flex items-center gap-1"
            >
              <Download size={11} weight="bold" /> PDF
            </button>
          </div>

          {/* XML key/value table (Recon.Report.5) */}
          {result.format === "xml" && result.parsed?.kv && (
            <div className="mb-3 border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid="tyro-recon-xml-table">
              <div className="text-[10px] font-semibold uppercase tracking-wider px-3 py-2 bg-[#F8FAFC] border-b border-[#E5E7EB] text-[#4B5563] inline-flex items-center gap-1.5">
                <FileCode size={11} weight="bold" /> Parsed XML
              </div>
              <table className="w-full text-[11px]">
                <tbody>
                  {result.parsed.kv.map((kv, i) => (
                    <tr key={i} className="border-b border-[#F1F5F9] last:border-0">
                      <td className="px-3 py-1.5 font-mono text-[#6B7280] align-top">{kv.key}</td>
                      <td className="px-3 py-1.5 font-mono text-[#121216] break-all">{kv.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Cleaned text body (Recon.Report.4) */}
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5 inline-flex items-center gap-1.5">
            <FileText size={11} weight="bold" />
            {result.format === "xml" ? "Raw XML" : "Report (formatting labels stripped)"}
          </div>
          <pre className="whitespace-pre-wrap text-[11px] font-mono bg-[#111826] text-[#E2E8F0] rounded-xl p-3 max-h-96 overflow-auto" data-testid="tyro-recon-output">
{result.format === "xml" ? (result.parsed?.pretty || result.raw) : (result.cleaned || "(empty report)")}
          </pre>

          {/* Quick toggle to view the raw, unstripped response — useful
              for cert auditors who want to see exactly what the SDK
              delivered including the MEDIUM_CENTRED labels. */}
          {result.format !== "xml" && result.raw !== result.cleaned && (
            <details className="mt-2">
              <summary className="text-[10px] text-[#6B7280] cursor-pointer hover:text-[#121216]">View raw SDK response (with formatting labels)</summary>
              <pre className="whitespace-pre-wrap text-[10px] font-mono bg-[#F8FAFC] border border-[#E5E7EB] rounded p-2 mt-1 max-h-60 overflow-auto">{result.raw}</pre>
            </details>
          )}
        </div>
      )}

      {/* ─── Recon.Report.2 — progress modal ────────────────────── */}
      {progress && (
        <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tyro-recon-progress">
          <div className="rounded-2xl bg-white shadow-2xl p-8 w-full max-w-sm text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#EFF6FF] flex items-center justify-center">
              <CircleNotch size={24} weight="bold" className="text-[#1E5FEB] animate-spin" />
            </div>
            <div className="font-display font-semibold text-[16px] tracking-tight text-[#121216]">Generating reconciliation…</div>
            <div className="mt-2 text-[12px] text-[#4B5563]">{progress.msg || "Please wait — Tyro is pulling the report from the acquirer."}</div>
            <div className="mt-3 text-[10px] font-mono text-[#9CA3AF]">
              {reportType.toUpperCase()} · {format.toUpperCase()} · {businessDay || lastBusinessDays[0].iso}
            </div>
            {/* Escape hatch — the SDK has a 60s internal timeout but the
                user shouldn't be locked into a modal if something
                upstream wedges. Clicking Cancel hides the modal
                immediately; the SDK call still races to completion in
                the background but its result is ignored once `progress`
                is null. */}
            <button
              onClick={() => setProgress(null)}
              className="mt-5 h-9 px-4 rounded-full bg-white border border-[#E5E7EB] text-[11px] uppercase tracking-[0.14em] font-bold text-[#4B5563] hover:bg-[#F3F4F6]"
              data-testid="tyro-recon-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ─── Recon.Report.3 — failure modal ─────────────────────── */}
      {errorBox && (
        <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tyro-recon-error" onClick={() => setErrorBox(null)}>
          <div className="rounded-2xl bg-white shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <Warning size={20} weight="fill" className="text-[#DC2626]" />
              </div>
              <button onClick={() => setErrorBox(null)} className="text-[#9CA3AF] hover:text-[#121216] p-1" data-testid="tyro-recon-error-close">
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="font-display font-semibold text-[18px] tracking-tight text-[#121216]">Reconciliation failed</div>
            <div className="mt-3 space-y-2">
              <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] p-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold mb-1">Result</div>
                <div className="font-mono text-[13px] font-bold text-[#DC2626]" data-testid="tyro-recon-error-result">{errorBox.result}</div>
              </div>
              <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] p-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280] font-semibold mb-1">Error message</div>
                <div className="font-mono text-[12px] text-[#121216] leading-relaxed break-words" data-testid="tyro-recon-error-message">{errorBox.error}</div>
              </div>

              {/* Friendly explanation for the common Tyro-side failures.
                  These are NOT bugs in our integration — they're the
                  acquirer telling us there's nothing to reconcile. */}
              {(() => {
                const msg = (errorBox.error || "").toLowerCase();
                const noData = msg.includes("could not retrieve") || msg.includes("no data") || msg.includes("no transactions") || msg.includes("not found");
                const terminalDown = msg.includes("terminal") && (msg.includes("unreachable") || msg.includes("offline") || msg.includes("not paired"));
                if (noData) {
                  return (
                    <div className="rounded-lg border border-[#DBEAFE] bg-[#EFF6FF] p-3 text-[12px] text-[#1E3A8A] leading-relaxed" data-testid="tyro-recon-error-hint">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#1E40AF] font-semibold mb-1">What this usually means</div>
                      Tyro has no settled transactions on this MID for <b>{businessDay || lastBusinessDays[0].iso}</b>{reportType === "detail" ? " (detail report)" : ""}. Try one of these:
                      <ul className="mt-2 ml-5 list-disc space-y-1 text-[#1E3A8A]">
                        <li>Switch <b>Report type</b> from <i>Detail</i> to <i>Summary</i> (most simulator MIDs only have totals, not per-tx detail).</li>
                        <li>Pick a different <b>Business day</b> — the simulator only carries data for days with prior approved transactions.</li>
                        <li>Run a quick test purchase first (e.g. $100 magic-approved), then retry.</li>
                      </ul>
                    </div>
                  );
                }
                if (terminalDown) {
                  return (
                    <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3 text-[12px] text-[#92400E] leading-relaxed" data-testid="tyro-recon-error-hint">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#92400E] font-semibold mb-1">What this usually means</div>
                      Pairing has expired or the simulator window was closed. Re-pair the terminal under <a className="underline font-semibold" href="/settings/tyro">/settings/tyro</a>, then retry.
                    </div>
                  );
                }
                return null;
              })()}

              {errorBox.raw && (
                <details>
                  <summary className="text-[10px] text-[#6B7280] cursor-pointer hover:text-[#121216]">Raw SDK payload</summary>
                  <pre className="text-[10px] font-mono bg-[#F8FAFC] border border-[#E5E7EB] rounded p-2 mt-1 max-h-48 overflow-auto">{JSON.stringify(errorBox.raw, null, 2)}</pre>
                </details>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              {(() => {
                const msg = (errorBox.error || "").toLowerCase();
                const noData = msg.includes("could not retrieve") || msg.includes("no data") || msg.includes("no transactions") || msg.includes("not found");
                if (noData && reportType === "detail") {
                  return (
                    <button
                      type="button"
                      onClick={() => { setReportType("summary"); setErrorBox(null); setTimeout(fetchReport, 0); }}
                      className="flex-1 h-10 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-bold transition-colors"
                      data-testid="tyro-recon-error-try-summary"
                    >
                      RETRY AS SUMMARY
                    </button>
                  );
                }
                return null;
              })()}
              <button
                type="button"
                onClick={() => setErrorBox(null)}
                className="flex-1 h-10 rounded-xl bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-bold transition-colors"
                data-testid="tyro-recon-error-dismiss"
              >
                DISMISS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
